import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Virus, CaretRight, ArrowRight, MapPin, Recycle, ShoppingBag, Package, Truck,
  Pill, Flask, Wheelchair, DotsNine, Wallet, Receipt, CreditCard, Plus, Lock, Check, User, Clock,
} from "@phosphor-icons/react";
import { custApi } from "../../lib/customerApi";

const card = "bg-white rounded-[22px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)]";
const ACTIVE_SR = ["NEW", "QUOTED", "APPROVED", "SCHEDULED", "IN_PROGRESS"];
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export const HomeGreeting = ({ name }) => {
  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  return (
    <section className="px-5 pt-4 me-fade-up" data-testid="me-home-greeting">
      <h1 className="font-display font-black text-[28px] leading-[1.1] tracking-tight text-[#111827]">
        {greet}, {name.charAt(0).toUpperCase() + name.slice(1)}.
      </h1>
      <p className="text-[16px] text-[#6B7280] mt-1">Your care, all in one place.</p>
    </section>
  );
};

export const BmwBookCard = ({ locked, gate }) => (
  <section className="px-5 mt-5 me-fade-up" style={{ animationDelay: "60ms" }} data-testid="me-bmw-card">
    <div className="rounded-[22px] bg-[#FFF7F7] border border-[#FDE8E8] p-4 shadow-[0_6px_24px_rgba(220,38,38,0.06)]">
      <Link to={locked ? gate : "/me/hygiene"} className="flex items-center gap-4" data-testid="me-bmw-open">
        <div className="w-16 h-16 rounded-2xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
          <Virus size={32} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[17px] text-[#111827] leading-tight">Biomedical Waste Collection</div>
          <div className="text-[13px] text-[#6B7280] mt-0.5">Safe doorstep collection</div>
        </div>
        <CaretRight size={18} className="text-[#9CA3AF]" />
      </Link>
      <Link to={locked ? gate : "/me/requests?new=1"} data-testid={locked ? "me-book-pickup-locked" : "me-book-pickup"}
            className="mt-4 h-12 rounded-2xl bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-[16px] flex items-center justify-center gap-2 shadow-[0_10px_24px_rgba(220,38,38,0.28)] active:scale-[0.98] transition-transform">
        {locked ? <><Lock size={16} weight="fill" /> Verify to book</> : <>Book a Pickup <ArrowRight size={16} weight="bold" /></>}
      </Link>
    </div>
  </section>
);

export const NextCollectionCard = ({ me, upcoming, srs }) => {
  const next = upcoming?.next || upcoming?.dates?.[0] || null;
  const dateStr = next ? (next.date || next) : null;
  const d = dateStr ? new Date(dateStr) : null;
  const booked = dateStr && srs.some(r => ACTIVE_SR.includes(r.status)
    && (r.scheduled_date || r.preferred_date) === dateStr);
  return (
    <section className="px-5 mt-4 me-fade-up" style={{ animationDelay: "120ms" }} data-testid="me-next-collection">
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#111827]">Next Collection</div>
          <Link to="/me/hygiene" className="text-[13px] font-semibold text-[#DC2626] inline-flex items-center gap-1" data-testid="me-view-schedule">
            View schedule <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
        {d ? (
          <Link to="/me/requests" className="mt-3 flex items-center gap-4" data-testid="me-next-collection-open">
            <div className="w-[72px] py-2 rounded-2xl bg-[#FFF1F1] text-center shrink-0">
              <div className="text-[11px] font-semibold text-[#DC2626] uppercase">{d.toLocaleDateString("en-IN", { weekday: "short" })}</div>
              <div className="font-display font-black text-[30px] leading-none text-[#DC2626]">{d.getDate()}</div>
              <div className="text-[11px] text-[#374151] uppercase">{d.toLocaleDateString("en-IN", { month: "short" })}</div>
            </div>
            <div className="flex-1 min-w-0 border-l border-[#F1EFEC] pl-4">
              <div className="flex items-center gap-1.5 font-semibold text-[15px] text-[#111827]">
                <MapPin size={15} className="text-[#374151]" />
                <span className="truncate" data-testid="me-next-zone">{me?.zone_name || me?.ward_name || "Your zone"}</span>
              </div>
              <div className="text-[13px] text-[#6B7280] mt-0.5" data-testid="me-next-date">
                {d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                {next.slot ? ` · ${next.slot}` : ""}
              </div>
              <span className={`mt-2 inline-block text-[12px] font-medium rounded-full px-3 py-1 ${booked ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FFF1F1] text-[#DC2626]"}`}
                    data-testid="me-next-booked">{booked ? "Pickup booked" : "No pickup booked"}</span>
            </div>
            <CaretRight size={18} className="text-[#374151]" />
          </Link>
        ) : (
          <div className="mt-3 text-[13px] text-[#6B7280]" data-testid="me-next-none">
            {upcoming?.warning || "No collection day scheduled for your area yet."}
          </div>
        )}
      </div>
    </section>
  );
};

const STEPS = ["Booked", "Assigned", "On the way", "Collected"];
const stepOf = (sr, t) => {
  const ts = t?.task?.status;
  if (sr.status === "COMPLETED" || ts === "completed") return 3;
  if (sr.status === "IN_PROGRESS" || ts === "in_progress" || t?.staff_location) return 2;
  if (t?.staff || sr.assigned_staff_id || sr.assigned_staff_name) return 1;
  return 0;
};

export const LivePickupCard = ({ srs, locked }) => {
  const today = todayIso();
  const active = srs
    .filter(r => ACTIVE_SR.includes(r.status)
      && (r.status === "IN_PROGRESS" || (r.scheduled_date || r.preferred_date) === today))
    .sort((a, b) => (a.status === "IN_PROGRESS" ? -1 : 0) - (b.status === "IN_PROGRESS" ? -1 : 0))[0];
  const [trk, setTrk] = useState(null);
  useEffect(() => {
    if (!active || locked) return;
    const load = () => custApi.get(`/me/tasks/${active.id}/tracking`).then(r => setTrk(r.data)).catch(() => {});
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(iv);
  }, [active?.id, locked]); // eslint-disable-line react-hooks/exhaustive-deps
  if (locked) return null;
  const step = active ? stepOf(active, trk) : -1;
  return (
    <section className="px-5 mt-4 me-fade-up" style={{ animationDelay: "180ms" }} data-testid="me-live-tracking">
      <div className={`${card} p-4`}>
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#111827] flex items-center gap-2">
            {active && <span className="relative flex w-2.5 h-2.5"><span className="absolute inset-0 rounded-full bg-[#DC2626] animate-ping opacity-60" /><span className="relative w-2.5 h-2.5 rounded-full bg-[#DC2626]" /></span>}
            Live Service Tracking
          </div>
          <Link to={active ? `/me/tracking/${active.id}` : "/me/tracking"} data-testid="me-live-open-map"
                className="text-[13px] font-semibold text-[#DC2626] inline-flex items-center gap-1">
            {active ? "Open map" : "History"} <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
        {!active ? (
          <div className="mt-3 flex items-center gap-3 text-[13px] text-[#6B7280]" data-testid="me-live-none">
            <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center"><Truck size={20} className="text-[#9CA3AF]" /></div>
            No active pickup today.
          </div>
        ) : (
          <>
            <div className="mt-3 text-[13px] text-[#374151]" data-testid="me-live-sr">
              <span className="font-semibold">{active.request_no || "Pickup"}</span>
              {(active.scheduled_date || active.preferred_date) && ` · ${active.scheduled_date || active.preferred_date}`}
            </div>
            <div className="mt-3 grid grid-cols-4 gap-1" data-testid="me-live-steps">
              {STEPS.map((l, i) => (
                <div key={l} className="flex flex-col items-center text-center" data-testid={`me-live-step-${i}`}>
                  <div className="flex items-center w-full">
                    <div className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : i <= step ? "bg-[#DC2626]" : "bg-[#E5E7EB]"}`} />
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white ${i <= step ? "bg-[#DC2626]" : "bg-[#E5E7EB]"} ${i === step ? "ring-4 ring-[#FEE2E2]" : ""}`}>
                      {i < step ? <Check size={14} weight="bold" /> : <span className="text-[11px] font-bold">{i + 1}</span>}
                    </div>
                    <div className={`h-0.5 flex-1 ${i === 3 ? "opacity-0" : i < step ? "bg-[#DC2626]" : "bg-[#E5E7EB]"}`} />
                  </div>
                  <div className={`text-[11px] mt-1 ${i <= step ? "text-[#111827] font-semibold" : "text-[#9CA3AF]"}`}>{l}</div>
                </div>
              ))}
            </div>
            {(trk?.staff || trk?.eta_minutes != null) && (
              <div className="mt-3 flex items-center gap-4 text-[13px] text-[#374151]">
                {trk?.staff?.name && <span className="inline-flex items-center gap-1" data-testid="me-live-staff"><User size={14} className="text-[#15803D]" />{trk.staff.name}</span>}
                {trk?.eta_minutes != null && <span className="inline-flex items-center gap-1" data-testid="me-live-eta"><Clock size={14} className="text-[#DC2626]" />ETA {Math.round(trk.eta_minutes)} min</span>}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

const TONES = {
  red: "bg-[#FFF1F1] text-[#DC2626]", blue: "bg-[#EFF6FF] text-[#2563EB]", green: "bg-[#ECFDF3] text-[#16A34A]",
  purple: "bg-[#F5F3FF] text-[#7C3AED]", orange: "bg-[#FFF7ED] text-[#EA580C]", amber: "bg-[#FFFBEB] text-[#B45309]",
  teal: "bg-[#F0FDFA] text-[#0F766E]", slate: "bg-[#F8FAFC] text-[#334155]",
};

export const QuickServices = ({ locked, gate, onRequestBags }) => {
  const tiles = [
    { key: "waste", label: "Book\nPickup", icon: Recycle, tone: "red", to: "/me/requests?new=1", hygiene: true },
    { key: "buy-bags", label: "All\nServices", icon: ShoppingBag, tone: "orange", to: "/me/services", hygiene: true },
    { key: "request-bags", label: "Request\nNew Bags", icon: Package, tone: "amber", onClick: onRequestBags, hygiene: true },
    { key: "tracking", label: "Live\nTracking", icon: Truck, tone: "teal", to: "/me/tracking", hygiene: true },
    { key: "medicines", label: "Order\nMedicines", icon: Pill, tone: "blue", to: "/me/services/pharmacy" },
    { key: "lab", label: "Lab\nTests", icon: Flask, tone: "green", to: "/me/health" },
    { key: "equipment", label: "Medical\nEquipment", icon: Wheelchair, tone: "purple", to: "/me/services/rentals" },
    { key: "more", label: "More\nServices", icon: DotsNine, tone: "slate", to: "/me/services" },
  ];
  return (
    <section className="px-5 mt-6 me-fade-up" style={{ animationDelay: "240ms" }} data-testid="me-quick-services">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-[18px] text-[#111827]">Quick Services</div>
        <Link to="/me/services" className="text-[13px] font-semibold text-[#DC2626] inline-flex items-center gap-1" data-testid="me-quick-view-all">
          View all <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <div className="grid grid-cols-4 gap-x-3 gap-y-4">
        {tiles.map(t => {
          const isLocked = locked && t.hygiene;
          const inner = (
            <>
              <div className={`relative w-full aspect-square max-w-[76px] rounded-[20px] flex items-center justify-center ${TONES[t.tone]} ${isLocked ? "opacity-60" : ""}`}>
                <t.icon size={28} weight="duotone" />
                {isLocked && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#F59E0B] text-white flex items-center justify-center ring-2 ring-white"><Lock size={10} weight="fill" /></span>}
              </div>
              <span className="text-[12px] text-[#374151] leading-tight text-center whitespace-pre-line">{t.label}</span>
            </>
          );
          const cls = "flex flex-col items-center gap-1.5 active:scale-[0.95] transition-transform";
          if (t.onClick && !isLocked) {
            return <button key={t.key} type="button" onClick={t.onClick} className={cls} data-testid={`me-qs-${t.key}`}>{inner}</button>;
          }
          return <Link key={t.key} to={isLocked ? gate : t.to} className={cls} data-testid={`me-qs-${t.key}`}>{inner}</Link>;
        })}
      </div>
    </section>
  );
};

export const WalletCard = ({ balance }) => (
  <section className="px-5 mt-6 me-fade-up" style={{ animationDelay: "300ms" }} data-testid="me-wallet-card">
    <div className={`${card} p-4`}>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#FFF1F1] text-[#DC2626] flex items-center justify-center shrink-0">
          <Wallet size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-[#6B7280]">Wallet Balance</div>
          <div className="font-display font-black text-[26px] text-[#111827] leading-tight" data-testid="me-wallet-balance">
            ₹{Number(balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <Link to="/me/wallet" className="h-10 px-4 rounded-xl bg-[#FFF1F1] text-[#DC2626] text-[14px] font-semibold inline-flex items-center gap-1" data-testid="me-wallet-add">
          <Plus size={14} weight="bold" /> Add Money
        </Link>
      </div>
      <div className="mt-4 pt-3 border-t border-[#F1EFEC] grid grid-cols-2 divide-x divide-[#F1EFEC]">
        <Link to="/me/wallet" className="flex items-center gap-2 text-[14px] font-medium text-[#DC2626]" data-testid="me-wallet-transactions">
          <Receipt size={20} /> Transactions <ArrowRight size={12} weight="bold" />
        </Link>
        <Link to="/me/dues" className="flex items-center gap-2 pl-4 text-[14px] font-medium text-[#DC2626]" data-testid="me-wallet-payments">
          <CreditCard size={20} /> Payments <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  </section>
);

export const DuesCard = ({ dues }) => !dues?.count ? null : (
  <section className="px-5 mt-4" data-testid="me-dues-alert">
    <Link to="/me/dues" className={`${card} p-4 flex items-center gap-4`}>
      <div className="w-12 h-12 rounded-2xl bg-[#FFF7ED] text-[#EA580C] flex items-center justify-center"><Receipt size={22} weight="duotone" /></div>
      <div className="flex-1">
        <div className="text-[13px] text-[#6B7280]">Outstanding dues · {dues.count} invoice{dues.count > 1 ? "s" : ""}</div>
        <div className="font-display font-black text-[20px] text-[#111827]">₹{Number(dues.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </div>
      <span className="text-[13px] font-semibold text-[#DC2626] inline-flex items-center gap-1">Pay <ArrowRight size={12} weight="bold" /></span>
    </Link>
  </section>
);

export const SectionLabel = ({ children }) => (
  <div className="px-5 mt-8 mb-1 text-[12px] font-semibold text-[#9CA3AF]">{children}</div>
);
